import { get, writable } from "svelte/store";

import length from "@turf/length";
import nearestPointOnLine from "@turf/nearest-point-on-line";
import { lineString, point } from "@turf/helpers";
import Mexp from "math-expression-evaluator/dist/browser/math-expression-evaluator.min.js";

import GeoHelper from "../../Helpers/GeoHelper";
import roundFloat from "../../Helpers/Misc/roundFloat";
import DistanceUnit from "./DistanceUnit";
import DistanceUtil from "./DistanceUtil";

import RangeRing from "./RangeRing/RangeRing";
import MapStore from "../../Map/Store";
import AuthStore from "../../Auth/Store";

class RoutePlotPoint {
	constructor() {
		this.position = { lng: 0, lat: 0 };
		this.positionStr = GeoHelper.lngLatCoordinatesToString([0, 0], get(AuthStore.session.user).lngLatFormat, 4);
		this.rangeRings = [];

		this.waitEnabled = false;
		this.waitHours = 0;
	}

	setPosition(lng, lat) {
		this.position.lng = roundFloat(lng, 4);
		this.position.lat = roundFloat(lat, 4);
		this.positionStr = GeoHelper.lngLatCoordinatesToString([this.position.lng, this.position.lat], get(AuthStore.session.user).lngLatFormat, 4);

		for (let i = 0; i < this.rangeRings.length; i++) {
			this.rangeRings[i].setPosition(this.position.lng, this.position.lat);
		}
	}

	addRangeRing() {
		const ring = new RangeRing(this.rangeRings.length);
		ring.setPosition(this.position.lng, this.position.lat);
		this.rangeRings.push(ring);
	}

	removeAllRangeRings() {
		this.rangeRings = [];
	}
}

class RoutePlotJourney {
	constructor() {
		this.by = "speed";
		this.speedKnots = 1;
		this.distance = {
			km: writable(0),
			miles: writable(0),
			nauticalMiles: writable(0)
		};
	}
}

class RoutePlot {
	constructor() {
		this.points = writable([]);
		this.journeys = writable([]);

		this.results = {
			distance: {
				km: writable(0),
				miles: writable(0),
				nauticalMiles: writable(0)
			},
			time: writable(0),
			errs: {
				time: writable("")
			}
		};
		this.lastUpdated = writable(Date.now());
	}

	setPointPosition(index, lng, lat) {
		const points = get(this.points);
		points[index].setPosition(lng, lat);
		this.points.set(points);
		this.lastUpdated.set(Date.now());
	}

	get pointsLength() {
		return get(this.points).length;
	}

	getPointAt(index) {
		return get(this.points)[index];
	}

	get linePoints() {
		return get(this.points).map((point) => {
			return [point.position.lng, point.position.lat];
		});
	}

	findPointByLngLat(lng, lat) {
		const points = get(this.points);
		for (let i = 0; i < points.length; i++) {
			if (points[i].position.lng === lng && points[i].position.lat === lat) {
				return i;
			}
		}
		return -1;
	}

	findClosestPointByLngLat(lng, lat) {
		const points = get(this.points);
		let closestPointIndex = null;
		let closestPointDistanceSq = RoutePlot.MAX_DISTANCE;
		for (let i = 0; i < points.length; i++) {
			const distSq = DistanceUtil.distanceSquared(lng, lat, points[i].position.lng, points[i].position.lat);
			if (distSq < closestPointDistanceSq) {
				closestPointDistanceSq = distSq;
				closestPointIndex = i;
			}
		}
		return closestPointIndex;
	}

	findClosestDistanceSquaredTo(lng, lat) {
		// TODO: consider using terf?
		const points = get(this.points);
		let closestPointDistanceSq = RoutePlot.MAX_DISTANCE;
		for (let i = 0; i < points.length; i++) {
			const distSq = DistanceUtil.distanceSquared(lng, lat, points[i].position.lng, points[i].position.lat);
			if (distSq < closestPointDistanceSq) {
				closestPointDistanceSq = distSq;
			}
		}
		return closestPointDistanceSq;
	}

	addPoint() {
		const point = new RoutePlotPoint();
		const journey = new RoutePlotJourney();

		const points = get(this.points);
		if (points.length > 0) {
			const bounds = MapStore.helpers.getMap().getBounds(); // LngLatBounds
			const cameraWidth = bounds.getEast() - bounds.getWest();
			const latestPoint = points[points.length - 1];

			point.setPosition(
				parseFloat(latestPoint.position.lng) + (parseFloat(cameraWidth) * 0.02),
				latestPoint.position.lat
			);
		}

		this.points.set([...points, point]);
		this.journeys.set([...get(this.journeys), journey]);

		this.lastUpdated.set(Date.now());
	}

	removePointByIndex(index) {
		const points = get(this.points);
		points.splice(index, 1);
		this.points.set(points);

		const journeys = get(this.journeys);
		journeys.splice(index, 1);
		this.journeys.set(journeys);

		this.lastUpdated.set(Date.now());
	}

	clearAllPoints() {
		this.points.set([]);
		this.journeys.set([]);
		this.lastUpdated.set(Date.now());
	}

	addPointAt(lng, lat) {
		const newPoint = new RoutePlotPoint();
		newPoint.setPosition(lng, lat);
		this.points.set([...get(this.points), newPoint]);

		const newJourney = new RoutePlotJourney();
		this.journeys.set([...get(this.journeys), newJourney]);

		this.lastUpdated.set(Date.now());
	}

	addPointBetweenAt(lng, lat) {
		const newPoint = new RoutePlotPoint();
		newPoint.setPosition(lng, lat);
		const newJourney = new RoutePlotJourney();

		const points = get(this.points);
		const journeys = get(this.journeys);
		const linePoints = this.linePoints;
		const line = lineString(linePoints);
		const targetPoint = point([lng, lat]);
		const snapped = nearestPointOnLine(line, targetPoint, { units: "miles" });
		const index = snapped.properties.index + 1;

		points.splice(index, 0, newPoint);
		journeys.splice(index, 0, newJourney);
		this.points.set(points);
		this.journeys.set(journeys);

		this.lastUpdated.set(Date.now());
	}

	removePointByLngLat(lng, lat) {
		const index = this.findPointByLngLat(lng, lat);
		if (index >= 0) {
			const points = get(this.points);
			points.splice(index, 1);
			this.points.set(points);

			const journeys = get(this.journeys);
			journeys.splice(index, 1);
			this.journeys.set(journeys);

			this.lastUpdated.set(Date.now());
		}
	}

	recalculateDistance() {
		console.log("recalculateDistance");
		const points = get(this.points);
		const journeys = get(this.journeys);
		if (points.length >= 2) {
			const linePoints = this.linePoints;
			const line = lineString(linePoints);
			const lenKM = length(line, { units: DistanceUnit.KILOMETERS });
			const lenMiles = length(line, { units: DistanceUnit.MILES });

			for (let i = 0; i < points.length - 1; i++) {
				const p1 = points[i];
				const p2 = points[i + 1];
				const line = lineString([[p1.position.lng, p1.position.lat], [p2.position.lng, p2.position.lat]]);
				const segmentLenKM = length(line, { units: DistanceUnit.KILOMETERS });
				const segmentLenMiles = length(line, { units: DistanceUnit.MILES });

				journeys[i].distance.km.set(segmentLenKM);
				journeys[i].distance.miles.set(segmentLenMiles);
				journeys[i].distance.nauticalMiles.set(DistanceUtil.convert(segmentLenMiles, DistanceUnit.MILES, DistanceUnit.NAUTICAL_MILES));
			}

			this.results.errs.time.set("");
			this.results.distance.km.set(lenKM);
			this.results.distance.miles.set(lenMiles);
			this.results.distance.nauticalMiles.set(DistanceUtil.convert(lenMiles, DistanceUnit.MILES, DistanceUnit.NAUTICAL_MILES));

			let totalTimeHours = 0;
			try {
				for (let i = 0; i < points.length; i++) {
					if (points[i].waitEnabled) {
						if (points[i].waitHours.length > 0) {
							const evalHours = Mexp.eval(points[i].waitHours);
							const hrs = parseFloat(evalHours);
							if (Number.isNaN(hrs)) {
								this.results.errs.time.set("Wait hours (" + hrs + ") is not valid.");
							}
							else {
								totalTimeHours += hrs;
							}
						}
					}
					if (i !== points.length - 1) {
						totalTimeHours += GeoHelper.calculateTime(points[i].position, points[i + 1].position, journeys[i].speedKnots, DistanceUnit.NAUTICAL_MILES);
					}
				}
			}
			catch (e) {
				console.warn("e", e);
				this.results.errs.time.set(e.message);
			}
			this.results.time.set(totalTimeHours);
		}
		else {
			this.results.errs.time.set("");
			this.results.time.set("");
		}
		this.lastUpdated.set(Date.now());
	}
}
RoutePlot.MAX_DISTANCE = 999999999;

export default RoutePlot;
